import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Grid, Switch, Typography } from "@mui/material";
import moment from "moment";
import AsyncSelect from "react-select/async";
import ReactDOM from "react-dom";
import {
  // languages,
  routingOptions,
  genderPreferenceOptions,
  ADDRESS_GEN_TYPE,
} from "../../../constants/config.constants";
import languages from '../../../constants/languages.json'
import IconComponent from "../../../components/DataTable/Icon";
import classNames from "classnames";
import {
  useGetLocationsMutation,
  useSuggestLocationsMutation,
  useAttachLocationMutation,
} from "../../../features/patient/patientApiSlice";
import useDebounce from "../../../hooks/useDebounce";
import Loading from "../../../components/Loading";
import ReusableModal from "../../../components/Modals/ReusableModal";
import LatLongDisplay from "../../CareManagement/Patient/components/LatLatLongDisplay";
import InfoIcon from '@mui/icons-material/Info';
import { selectUser } from "../../../features/auth/authSlice";
import { useSelector } from "react-redux";

const CustomOption = ({ innerProps, label, data, isFocused, isSelected }) => (
  <div
    {...innerProps}
    className={`custom-option ${isFocused ? "focused" : ""} ${isSelected ? "selected" : ""}`}
    key={label}
  >
    {label.split("|").map((value, i) => {
      return (
        <Typography
          key={value}
          className={`${i === 0 ? "bold-600" : "font-14"}`}
        >
          {value.split(":").length < 2 ? (
            value
          ) : (
            <Grid className="flex-row gap-1 d-flex">
              {value.split(":").map((data, i) => (
                <Typography
                  key={data}
                  className={`${i === 0 ? "bold-bold" : ""}`}
                  style={{
                    fontSize: 14,
                    color: isSelected ? "#fff" : "rgba(0,0,0,0.6)",
                  }}
                >
                  {data}
                  {i === 0 && ":"}
                </Typography>
              ))}
            </Grid>
          )}
        </Typography>
      );
    })}
  </div>
);

const FilterComponentV2 = ({
  patients,
  selectedPatient,
  setLanguagePreference,
  setGenderPreference,
  setSelectedPatient,
  setSelectedProvider,
  setStartDate,
  setEndDate,
  setAppointmentType,
  setAppointmentVisitType,
  providers,
  appointmentTypes,
  selectedProvider,
  startDate,
  endDate,
  appointmentType,
  onSubmitFilters,
  providerPromiseOptions,
  patientPromiseOptions,
  visitTypePromiseOptions,
  visitTypeFetchImmediate,
  prospectType,
  setProspectType,
  routingCriteria,
  setRoutingCriteria,
  languagePreference,
  genderPreference,
  onResetFilters,
  providerType,
  setProviderType,
  timeOfDay,
  setTimeOfDay,
  prospectTypeOptions,
  providerTypeOptions,
  timeOfDayOptions,
  selectedVisitLocation,
  setSelectedVisitLocation,
  setSearchConsecutiveSlots,
  searchConsecutiveSlots,
  showAlert
}) => {
  const user = useSelector(selectUser);
  // Address management state
  const [changeAddressModalOpen, setChangeAddressModalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [address, setAddress] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [suggestedLocations, setSuggestedLocations] = useState([]);
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(false);
  const [inputRect, setInputRect] = useState(null);
  const [manualAddress, setManualAddress] = useState(null);
  const [useManualEntry, setUseManualEntry] = useState(false);


  const addressInputRef = useRef(null);
  const addressSearch = useDebounce(address, 750);

  // Update input position when suggestions appear
  useEffect(() => {
    if (suggestedLocations.length > 0 && addressInputRef.current) {
      const rect = addressInputRef.current.getBoundingClientRect();
      setInputRect(rect);
    }
  }, [suggestedLocations.length]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target)
      ) {
        // Check if click is not on the suggestions dropdown
        const isClickOnSuggestions = event.target.closest(
          "[data-suggestions-dropdown]"
        );
        if (!isClickOnSuggestions) {
          setSuggestedLocations([]);
        }
      }
    };

    if (suggestedLocations.length > 0) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [suggestedLocations.length]);

  const [getLocations, { isLoading: locationsLoading }] =
    useGetLocationsMutation();
  const [suggestLocations, { isLoading: suggestLocationsLoading }] =
    useSuggestLocationsMutation();
  const [attachLocation, { isLoading: attachLocationLoading }] =
    useAttachLocationMutation();

  const loading =
    locationsLoading || suggestLocationsLoading || attachLocationLoading;

  // Fetch patient locations when patient is selected
  useEffect(() => {
    const patientId = selectedPatient?.id || selectedPatient?.value;
    if (patientId) {
      console.log(
        "Fetching locations for patient:",
        patientId,
        selectedPatient
      );
      getLocationsData();
    } else {
      setLocations([]);
      setSelectedVisitLocation(null);
    }
  }, [selectedPatient?.id, selectedPatient?.value]);

  // Suggest locations based on address search
  useEffect(() => {
    if (addressSearch) {
      suggestLocationsData();
    } else {
      setSuggestedLocations([]);
    }
  }, [addressSearch]);

  const getLocationsData = async () => {
    try {
      const patientId = selectedPatient?.id || selectedPatient?.value;
      if (!patientId) {
        console.error("No patient ID found");
        return;
      }

      const { data } = await getLocations({
        patientId: patientId,
      }).unwrap();

      const locationsData = data.map((x) => ({
        ...x.location,
        addressType: x.type,
        zip: x.zip ?? x?.location?.zip,
        address2: x.address2 ?? x?.location?.address2,
      }));

      console.log("Locations fetched:", locationsData);

      setSelectedVisitLocation(
        locationsData.find((x) => x.addressType === "PRIMARY") || null
      );

      setLocations(locationsData);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const suggestLocationsData = async () => {
    try {
      const { data } = await suggestLocations({
        address: addressSearch,
      }).unwrap();

      setSuggestedLocations(data);
    } catch (error) {
      console.error("Error suggesting locations:", error);
    }
  };

  const attachLocationData = async () => {
    try {
      const patientId = selectedPatient?.id || selectedPatient?.value;
      if (!patientId) {
        console.error("No patient ID found");
        return;
      }

      // Use manual address if available, otherwise use selected address
      const addressData = useManualEntry && manualAddress
        ? {
          locationId: null,
          street: manualAddress.street,
          city: manualAddress.city,
          state: manualAddress.state,
          county: null,
          zip: manualAddress.zip,
          latitude: null,
          longitude: null,
        }
        : {
          locationId: selectedAddress.placeId,
          street: selectedAddress.street,
          city: selectedAddress.city,
          state: selectedAddress.state,
          county: selectedAddress.county,
          zip: selectedAddress.zip,
          latitude: selectedAddress.lat,
          longitude: selectedAddress.lng,
        };

      const result = await attachLocation({
        patientId: patientId,
        ...addressData,
        saveAddressToProfile: saveAddressToProfile,
        manualAddress: useManualEntry ? manualAddress : null,
      }).unwrap();

      const newLocation = {
        id: addressData.locationId || result?.data?.id,
        street: addressData.street,
        city: addressData.city,
        state: addressData.state,
        zip: addressData.zip,
        latitude: result?.data?.latitude || addressData.latitude,
        longitude: result?.data?.longitude || addressData.longitude,
        county: result?.data?.county || addressData.county,
        addressType: "SECONDARY",
      };

      setLocations([...locations, newLocation]);
      setSelectedVisitLocation(newLocation);
      setSelectedAddress(null);
      setAddress("");
      setSaveAddressToProfile(false);
      setUseManualEntry(false);
      setManualAddress(null);
      setChangeAddressModalOpen(false);
    } catch (error) {
      showAlert(error?.data?.message);
      console.error("Error attaching location:", error);
    }
  };

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    const tomorrow = moment().add(1, 'day').format("YYYY-MM-DD");
    const defaultEndDate = moment().add(28, 'days').format("YYYY-MM-DD");
    const tomorrowEndDate = moment().add(1, 'day').add(28, 'days').format("YYYY-MM-DD");

    // User has permission: start date = tomorrow, end date = tomorrow + 28 days (fixed)
    if (user?.permissions?.includes('dispatch.edit_dispatch_end_date')) {
      setStartDate(tomorrow);
      setEndDate(tomorrowEndDate);
    }
    // User doesn't have permission: start date = today, end date editable
    else {
      if (!startDate) setStartDate(today);
      if (!endDate) setEndDate("");
    }
  }, [user]);

  return (
    <div className="flex items-end justify-start gap-10 mx-8 p-8 bg-[#FFF] rounded-md shadow-sm border border-gray-200 flex-wrap">

      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-row gap-4 items-end">
          {/* {user?.permissions?.includes('dispatch.select_patient') &&  */}
          <div className="flex relative flex-col gap-2 w-3/12">
            <div className="flex flex-row gap-3 justify-between items-end">
              <label
                htmlFor="patientSelect"
                className="text-sm font-bold whitespace-nowrap"
              >
                Select Patient <span className="text-red-500">*</span>
              </label>
              {user?.permissions?.includes('dispatch.patient_type_selection_dispatch') &&
                <Select
                  className="min-w-[120px]"
                  options={prospectTypeOptions}
                  placeholder="Prospect Type"
                  value={prospectType}
                  onChange={(e) => {
                    setProspectType(e);
                    setSelectedPatient("");
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      height: "32px",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: "32px",
                      padding: "0 6px",
                    }),
                    input: (base) => ({
                      ...base,
                      margin: "0px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: "32px",
                    }),
                  }}
                />}
            </div>
            <div className="flex flex-row gap-2 w-full">
              <AsyncSelect
                className="w-full"
                cacheOptions
                isClearable
                defaultOptions={patients}
                loadOptions={patientPromiseOptions}
                value={selectedPatient}
                isDisabled={!user?.permissions?.includes('dispatch.select_patient')}
                onChange={(selectedOption) => {
                  setSelectedPatient(selectedOption);
                  const langFromPatient =
                    selectedOption?.languagePreference != null
                      ? [
                          {
                            label: selectedOption.languagePreference,
                            value: selectedOption.languagePreference,
                          },
                        ]
                      : selectedOption?.languages?.length
                        ? selectedOption.languages.map((lang) => ({
                            label: lang,
                            value: lang,
                          }))
                        : [{ label: "English", value: "English" }];
                  setLanguagePreference(langFromPatient);
                }}
                onInputChange={(newValue, { action }) => {
                  if (action === "clear" || action === "input-change") {
                    patientPromiseOptions(newValue);
                  }
                }}
                placeholder="Search by name or DOB"
                components={{ Option: CustomOption }} // Use the custom option component
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                onBlur={(e) => {
                  patientPromiseOptions("");
                }}
              />
            </div>
            {selectedPatient && (
              <div className="absolute left-0 -bottom-14 w-full">
                {selectedVisitLocation ? (
                  <button
                    onClick={() => setChangeAddressModalOpen(true)}
                    className="flex justify-between items-center p-2 w-full text-sm bg-gray-200 rounded-md transition-colors hover:bg-gray-300"
                  >
                    <p className="m-0 text-xs font-semibold text-left text-primaryblack">
                      {selectedVisitLocation.street},{" "}
                      {selectedVisitLocation.city},{" "}
                      {selectedVisitLocation.state} {selectedVisitLocation.zip}{" "}
                      ({selectedVisitLocation.addressType})
                    </p>
                    <IconComponent
                      tooltip="Edit visit location"
                      icon={"Edit_Icon"}
                      i={selectedPatient}
                    />
                  </button>
                ) : (
                  <button
                    onClick={() => setChangeAddressModalOpen(true)}
                    className="flex justify-between items-center p-2 w-full text-sm bg-blue-100 rounded-md border border-blue-300 transition-colors hover:bg-blue-200"
                  >
                    <div className="font-semibold text-blue-700">
                      {locationsLoading
                        ? "Loading locations..."
                        : "Add Visit Location"}
                    </div>
                    <IconComponent
                      tooltip="Add visit location"
                      icon={"Edit_Icon"}
                      i={selectedPatient}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
           {/* } */}
          {user?.permissions?.includes('dispatch.select_provider') && <div className="flex flex-col gap-2 w-3/12">
            <label htmlFor="patientSelect" className="text-sm font-bold">
              Select Provider
            </label>
            <AsyncSelect
              className="w-full"
              cacheOptions
              defaultOptions={providers}
              loadOptions={providerPromiseOptions}
              // getOptionValue={(option) => `${option.value.id}-${option.value.provider_market_id}`}
              // getOptionLabel={(option) => option.label}
              isClearable
              value={selectedProvider}
              onChange={(selectedOption) => {
                setSelectedProvider(selectedOption);
              }}
              onInputChange={(newValue, { action }) => {
                if (action === "clear" || action === "input-change") {
                  providerPromiseOptions(newValue);
                }
              }}
              onBlur={(e) => {
                providerPromiseOptions("");
              }}
              placeholder="Type to search..."
              styles={{
                menu: (base) => ({
                  ...base,
                  zIndex: 999999,
                }),
              }}
            />
          </div>}
          {/* {user?.permissions?.includes('dispatch.select_provider_type') &&  */}
          <div className="flex flex-col gap-2 w-2/12">
            <label htmlFor="patientSelect" className="text-sm font-bold">
              Provider Type <span className="text-red-500">*</span>
            </label>
            <Select
              className="w-full"
              options={providerTypeOptions}
              placeholder="Provider Type"
              value={providerType}
              onChange={(selectedOption) => {
                setProviderType(selectedOption);
              }}
            />
          </div>
          {/* } */}
          {user?.permissions?.includes('dispatch.start_date') &&
            <div className="flex flex-col gap-2 w-2/12">
              <label htmlFor="patientSelect" className="text-sm font-bold">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                defaultValue={null}
                placeholder="dd/mm/yyyy"
                // min={moment().add(1, "day").format("YYYY-MM-DD")}
                // onChange={(e) => {
                //   setStartDate(e.target.value);
                //   console.log(e.target.value, "e.target.value");
                // }}

                min={user?.permissions?.includes('dispatch.edit_dispatch_end_date')
                  ? moment().add(1, 'day').format("YYYY-MM-DD")
                  : moment().format("YYYY-MM-DD")}
                max={endDate ? moment(endDate).subtract(1, 'day').format("YYYY-MM-DD") : undefined}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  setStartDate(newStartDate);

                  // If user has permission, automatically update end date to be start date + 28 days

                }}
                className="w-full h-[38px] p-2 border rounded-md border-primarygrayborder"
              />
            </div>
          }
          {user?.permissions?.includes('dispatch.end_date') && <div className="flex flex-col gap-2 w-2/12">
            <div className="flex justify-between">
              <label htmlFor="patientSelect" className="text-sm font-bold">
                End Date <span className="text-red-500">*</span>
              </label>
              {!user?.permissions?.includes('dispatch.edit_dispatch_end_date') && (
                <button
                  onClick={() => {
                    if (startDate)
                      setEndDate(
                        moment(startDate).add(27, "days").format("YYYY-MM-DD")
                      );
                    else
                      setEndDate(moment().add(27, "days").format("YYYY-MM-DD"));
                  }}
                >
                  <p className="m-0 text-sm font-medium text-primaryblue">
                    28-day window
                  </p>
                </button>)}
            </div>
            <input
              type="date"
              value={endDate}
              defaultValue={null}
              placeholder="dd/mm/yyyy"
              // min= {moment().add(1, "day").format("YYYY-MM-DD")}
              min={user?.permissions?.includes('dispatch.edit_dispatch_end_date')
                ? moment().add(1, 'day').add(28, 'days').format("YYYY-MM-DD")
                : moment().add(1, 'day').format("YYYY-MM-DD")}
              onChange={(e) => {
                if (!user?.permissions?.includes('dispatch.edit_dispatch_end_date')) {
                  setEndDate(e.target.value);
                }
              }}
              readOnly={user?.permissions?.includes('dispatch.edit_dispatch_end_date')}
              className="w-full h-[38px] p-2 border rounded-md border-primarygrayborder"
            />
          </div>}
        </div>
        <div
          className={classNames(
            "flex flex-row gap-4 items-cente",
            selectedPatient ? "mt-10" : "mt-0"
          )}
        >
          {/* {user?.permissions?.includes('dispatch.select_time_of_day') &&  */}
          <div className="flex flex-col gap-2 w-3/12">
            <label htmlFor="patientSelect" className="text-sm font-bold">
              Time of Day
            </label>
            <Select
              className="w-full"
              options={timeOfDayOptions}
              placeholder="Time of Day"
              value={timeOfDay}
              onChange={(selectedOption) => {
                setTimeOfDay(selectedOption);
              }}
            />
          </div>
          {/* } */}
          {user?.permissions?.includes('dispatch.visit_type') && <div className="flex flex-col gap-2 w-3/12">
            <label htmlFor="appoitmentType" className="text-sm font-bold">
              Visit Type <span className="text-red-500">*</span>
            </label>
            <AsyncSelect
              className="w-full"
              cacheOptions
              defaultOptions={
                selectedPatient?.value != null ? appointmentTypes : false
              }
              loadOptions={visitTypePromiseOptions}
              value={appointmentType}
              isDisabled={selectedPatient?.value == null}
              placeholder={
                selectedPatient?.value != null
                  ? "Visit Type"
                  : "Select a patient first"
              }
              onChange={(selectedOption) => {
                setAppointmentType(selectedOption);
                setAppointmentVisitType(selectedOption?.value);
              }}
              onInputChange={(newValue, { action }) => {
                if (selectedPatient?.value == null) return;
                if (
                  visitTypeFetchImmediate &&
                  (action === "input-change" || action === "clear") &&
                  newValue === ""
                ) {
                  visitTypePromiseOptions.cancel?.();
                  visitTypeFetchImmediate({
                    value: "",
                    patientId: selectedPatient.value,
                  });
                }
              }}
              onBlur={() => {
                if (selectedPatient?.value == null) return;
                visitTypePromiseOptions.cancel?.();
                visitTypeFetchImmediate?.({
                  value: "",
                  patientId: selectedPatient.value,
                });
              }}
              styles={{
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          </div>}
          {user?.permissions?.includes('dispatch.routing_criteria') && <div className="flex flex-col gap-2 w-2/12">
            <label htmlFor="appoitmentType" className="text-sm font-bold">
              Routing Criteria <span className="text-red-500">*</span>
            </label>
            <Select
              className="w-full"
              options={routingOptions}
              isSearchable
              value={routingCriteria}
              onChange={(selectedOption) => {
                setRoutingCriteria(selectedOption);
              }}
              styles={{
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          </div>}
          {user?.permissions?.includes('dispatch.provider_gender_preference') && <div className="flex flex-col gap-2 w-2/12">
            <label htmlFor="genderPreference" className="text-sm font-bold">
              Provider gender preference
            </label>
            <div className="w-full">
              <Select
                options={genderPreferenceOptions}
                isSearchable
                // isClearable
                value={genderPreference}
                onChange={(selectedOption) => {
                  setGenderPreference(selectedOption);
                }}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>}
          {user?.permissions?.includes('dispatch.language_preference') && <div className="flex flex-col gap-2 w-2/12">
            <label htmlFor="languagePreference" className="text-sm font-bold">
              Language preference
            </label>
            <div className="w-full">
              <Select
                options={
                  selectedPatient?.languages
                    ? selectedPatient?.languages
                      .map((language) => ({
                        label: language,
                        value: language,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label))
                    : languages
                      .filter((language) => language.english === "English")
                      .map((language) => ({
                        label: language.english,
                        value: language.english,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label))
                }
                isMulti
                isSearchable
                isClearable
                value={languagePreference}
                onChange={(selectedOption) => {
                  setLanguagePreference(selectedOption);
                }}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>}
        </div>
        <div className="flex flex-row gap-4 justify-end items-center">
          {/* {user?.permissions?.includes('dispatch.search_consecutive_slots') &&  */}
          <div className="flex flex-col gap-2">
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={searchConsecutiveSlots}
                onChange={(e) => setSearchConsecutiveSlots(e.target.checked)}
              />

              <span className="text-sm font-medium">
                Search for consecutive slots
              </span>

              {/* Info Icon with Tooltip */}
              <div className="relative group">
                {/* <InfoIcon  /> */}
                <div className="flex justify-center items-center w-5 h-5 text-sm font-bold text-gray-600 bg-gray-300 rounded-full cursor-pointer">
                  i
                </div>

                <div className="hidden absolute left-1/2 z-10 p-2 mt-2 w-64 text-xs text-white bg-gray-800 rounded shadow-lg -translate-x-1/2 group-hover:block">
                  Finds back-to-back appointment slots for household members
                  (e.g., married couples)
                </div>
              </div>
            </label>
          </div>
          {/* } */}
          {user?.permissions?.includes('dispatch.apply_filters') &&
            <div className="flex flex-col gap-2 w-2/12">
              <button
                onClick={(e) => onSubmitFilters(e, true, true)}
                className="flex relative items-center justify-center gap-2 py-2 text-white rounded-md border-[1px] border-primaryblue bg-primaryblue"
              >
                <span>Apply Filters</span>
              </button>
            </div>}
          {user?.permissions?.includes('dispatch.reset_filters') &&
            <div className="flex flex-col gap-2 w-2/12">
              <button
                onClick={async () => {
                  onResetFilters();
                  patientPromiseOptions("");
                  providerPromiseOptions("");
                  visitTypePromiseOptions.cancel?.();
                }}
                className="flex relative items-center justify-center gap-2 py-2 text-primaryblue rounded-md border-[1px] border-gray-300 bg-white"
              >
                <span>Reset Filters</span>
              </button>
            </div>}

        </div>
        {loading && <Loading loading={loading} />}
      </div>

      <ReusableModal
        hideHeader
        hideFooter
        open={changeAddressModalOpen && selectedPatient}
        onClose={() => {
          setChangeAddressModalOpen(false);
          setSelectedAddress(null);
          setAddress("");
          setSaveAddressToProfile(false);
          setUseManualEntry(false);
          setManualAddress(null);
        }}
        title=""
        maxWidth="md"
        boxClasses="w-full w-3xl"
      >
        <div className="flex flex-col gap-3 p-4 bg-amber-50 border-[1px] border-amber-400 rounded-md max-h-[70vh] overflow-y-auto">
          <div className="flex justify-between items-center">
            <p className="m-0 text-sm font-semibold text-amber-900 capitalize">
              Select or enter address
            </p>
            <button
              onClick={() => {
                setChangeAddressModalOpen(false);
                setSelectedAddress(null);
                setAddress("");
                setSaveAddressToProfile(false);
                setUseManualEntry(false);
                setManualAddress(null);
              }}
            >
              <p className="m-0 text-xs font-semibold text-gray-500 underline">
                Cancel
              </p>
            </button>
          </div>
          {locations.length > 0 && (
            <div className="flex flex-col gap-2">
              {locations.map((x) => (
                <button
                  key={x.id}
                  onClick={() => {
                    setSelectedVisitLocation(x);
                    setChangeAddressModalOpen(false);
                  }}
                  className={classNames(
                    "flex flex-col gap-2 items-start p-2 w-full rounded-md",
                    selectedVisitLocation?.id === x.id
                      ? "border-[1px] border-primaryblue bg-subtleblue"
                      : "border-[1px] border-primarygrayborder bg-white"
                  )}
                >
                  <p className="m-0 text-xs font-semibold text-left text-gray-500">
                    {x.addressType}
                  </p>
                  <p className="m-0 text-xs font-semibold text-left text-primaryblack">
                    {x.street}, {x.city}, {x.state} {x.zip}
                  </p>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <p className="m-0 text-xs font-semibold text-left text-gray-500 capitalize">
              Or enter a new address
            </p>

            {/* Manual Entry Checkbox */}
            <div className="flex gap-2 items-center">
              <input
                type="checkbox"
                id="manualEntryCheckbox"
                checked={useManualEntry}
                onChange={(e) => {
                  setUseManualEntry(e.target.checked);
                  if (!e.target.checked) {
                    setManualAddress(null);
                  } else {
                    setAddress("");
                    setSelectedAddress(null);
                    setSuggestedLocations([]);
                  }
                }}
                className="w-4 h-4 cursor-pointer"
              />
              <label
                htmlFor="manualEntryCheckbox"
                className="m-0 text-xs text-gray-700 cursor-pointer"
              >
                Turn off auto-fill address
              </label>
            </div>

            {/* Conditional rendering: either manual entry or search */}
            {useManualEntry ? (
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  placeholder="Street"
                  className="p-2 w-full text-sm rounded-md border border-primarygrayborder"
                  value={manualAddress?.street || ""}
                  onChange={(e) =>
                    setManualAddress({
                      ...manualAddress,
                      street: e.target.value,
                    })
                  }
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="City"
                    className="p-2 w-full text-sm rounded-md border border-primarygrayborder"
                    value={manualAddress?.city || ""}
                    onChange={(e) =>
                      setManualAddress({
                        ...manualAddress,
                        city: e.target.value,
                      })
                    }
                  />
                  <input
                    type="text"
                    placeholder="State"
                    className="p-2 w-1/3 text-sm rounded-md border border-primarygrayborder"
                    value={manualAddress?.state || ""}
                    onChange={(e) =>
                      setManualAddress({
                        ...manualAddress,
                        state: e.target.value,
                      })
                    }
                  />
                </div>
                <input
                  type="text"
                  placeholder="Zip Code"
                  className="p-2 w-full text-sm rounded-md border border-primarygrayborder"
                  value={manualAddress?.zip || ""}
                  onChange={(e) =>
                    setManualAddress({
                      ...manualAddress,
                      zip: e.target.value,
                    })
                  }
                />
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={addressInputRef}
                  type="text"
                  placeholder="Enter address"
                  className="p-2 w-full text-sm rounded-md border border-primarygrayborder"
                  value={
                    address !== ""
                      ? address
                      : selectedAddress !== null
                        ? `${selectedAddress?.street}, ${selectedAddress?.city}, ${selectedAddress?.state} ${selectedAddress?.zip}`
                        : ""
                  }
                  onChange={(e) => {
                    setAddress(e.target.value);
                    // Clear selected address when user starts typing or deletes text
                    if (selectedAddress !== null) {
                      setSelectedAddress(null);
                    }
                  }}
                />
                {suggestedLocations.length > 0 &&
                  inputRect &&
                  ReactDOM.createPortal(
                    <div
                      data-suggestions-dropdown
                      className="flex overflow-y-auto flex-col gap-2 mt-1 w-full max-h-28 bg-white rounded-md border shadow-lg"
                      style={{
                        position: "fixed",
                        top: `${inputRect.bottom + 4}px`,
                        left: `${inputRect.left}px`,
                        width: `${inputRect.width}px`,
                        zIndex: 10000,
                      }}
                    >
                      {suggestedLocations.map((x) => (
                        <button
                          key={x.id}
                          onClick={() => {
                            setSelectedAddress(x);
                            setAddress("");
                            setSuggestedLocations([]);
                          }}
                          className="flex flex-col gap-4 items-start p-2 w-full border-b border-primarygrayborder"
                        >
                          <p className="m-0 text-xs font-semibold text-left text-primaryblack">
                            {x.street}, {x.city}, {x.state} {x.zip}
                          </p>
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
              </div>
            )}
            {/* {(selectedAddress !== null || (useManualEntry && manualAddress)) && (
              <div className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  id="saveAddressCheckbox"
                  checked={saveAddressToProfile}
                  onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                />
                <label
                  htmlFor="saveAddressCheckbox"
                  className="m-0 text-xs text-gray-700 cursor-pointer"
                >
                  Save this to patient's profile for future use
                </label>
              </div>
            )} */}

            {/* LatLongDisplay Component Integration */}
            {(selectedAddress !== null || selectedVisitLocation != null || (useManualEntry && manualAddress)) && (
              <div className="mt-2">
                <LatLongDisplay
                  isFormDispatch={true}
                  latitude={
                    selectedVisitLocation?.latitude || null
                  }
                  longitude={
                    selectedVisitLocation?.longitude || null
                  }
                  loading={false}
                  editable={true}
                  address={{
                    id: selectedVisitLocation?.id,
                    city: selectedVisitLocation?.city,
                    state: selectedVisitLocation?.state,
                    coord_generation_type: selectedVisitLocation?.coord_generation_type
                  }}
                  patientId={selectedPatient?.id || selectedPatient?.value}
                  onAddressUpdate={(updatedLocation) => {
                    // Update coordinates when LatLongDisplay saves them
                    if (useManualEntry) {
                      setManualAddress(prev => ({
                        ...prev,
                        latitude: updatedLocation.latitude,
                        longitude: updatedLocation.longitude
                      }));
                    } else {
                      // selectedAddress !== null ?
                      //   selectedAddress(prev => ({
                      //     ...prev,
                      //     lat: updatedLocation.latitude,
                      //     lng: updatedLocation.longitude
                      //   }))
                      //   :
                      setSelectedVisitLocation(prev => ({
                        ...prev,
                        latitude: updatedLocation.latitude,
                        longitude: updatedLocation.longitude
                      }));
                    }
                  }}
                />
              </div>
            )}
            {(selectedAddress !== null || (useManualEntry && manualAddress)) && (
              <button
                onClick={attachLocationData}
                disabled={
                  useManualEntry &&
                  (!manualAddress?.street ||
                    !manualAddress?.city ||
                    !manualAddress?.state ||
                    !manualAddress?.zip)
                }
                className={classNames(
                  "flex w-full justify-between items-center text-sm border-[2px] rounded-md px-2 py-1 text-white text-center",
                  useManualEntry &&
                    (!manualAddress?.street ||
                      !manualAddress?.city ||
                      !manualAddress?.state ||
                      !manualAddress?.zip)
                    ? "bg-gray-400 border-gray-400 cursor-not-allowed"
                    : "bg-amber-500 border-amber-500"
                )}
              >
                <p className="m-0 mx-auto font-semibold">Save Address</p>
              </button>
            )}
          </div>
        </div>
      </ReusableModal>
    </div>
  );
};

export default FilterComponentV2;
